import { ReceiverKpiContainer } from '@apps-common/apps/portal/pages/receivers/components'
import { SelectInputSM } from '@apps-common/components/elements/form'
import { ModalBody, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { INPUT_CLASSES, INPUT_CONTAINER_CLASSES } from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { RECEIVERS_QUEUE_SIZE_OPTIONS } from '@apps-common/config/receivers'
import { sortAlphabetically } from '@apps-common/helpers/array-sort'
import { useReceiversFilterStore } from '@apps-common/stores'
import { ICloudReceiverModel } from '@apps-common/types/portal/cloud-receiver'
import { ButtonRoundSm } from '../elements'
import { VertSpacerLg } from '../layout'
import { FilterModalContainer } from './filter/FilterModalContainer'

export interface IReceiverFilterModal {
  receivers: ICloudReceiverModel
}

export const ReceiversFilterModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IReceiverFilterModal
}) => {
  const { receivers } = componentProps
  const state = useReceiversFilterStore()

  const servers = (receivers?.gateways || [])
    .reduce((acc: { label: string; value: string }[], curr) => [...acc, { label: curr.serverId, value: curr.serverId }], [])
    .filter((value, index, self) => self.findIndex((s) => s.value === value.value) === index)
    .sort((a, b) => sortAlphabetically(a, b, 'label'))

  const companies = (receivers?.gateways || [])
    .reduce(
      (acc: { label: string; value: string }[], curr) => [
        ...acc,
        ...curr.companies.map((c) => ({ label: c.companyName, value: c.companyId }))
      ],
      []
    )
    .filter((value, index, self) => self.findIndex((s) => s.value === value.value) === index)
    .sort((a, b) => sortAlphabetically(a, b, 'label'))

  return (
    <FilterModalContainer onDismiss={onDismiss}>
      <ModalHeaderContainer classes='!pb-[10px]'>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Receivers' title='Filter' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            <ButtonRoundSm
              type='button'
              className='action-brand'
              callback={() => {
                state.clear()
                onDismiss(null, ModalDismiss.Confirm)
              }}
            >
              <commonIcons.reload />
            </ButtonRoundSm>
            <ButtonRoundSm
              type='button'
              className='action-brand-solid'
              callback={() => {
                onDismiss(null, ModalDismiss.Cancel)
              }}
            >
              <commonIcons.close />
            </ButtonRoundSm>
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[75px] !pb-[30px] sm:!pb-[20px]'>
        <ReceiverKpiContainer loading={!state.data} />
        <VertSpacerLg />
        <div className='grid gap-[10px]'>
          <SelectInputSM
            label='Receiver'
            id='receivers-filter-receiver'
            showLabel
            className={INPUT_CLASSES}
            containerClassName={INPUT_CONTAINER_CLASSES}
            options={[{ label: 'Select Receiver', value: '' }, ...servers]}
            disabled={!receivers}
            hideError
            registration={{ value: state.serverId, onChange: (e: any) => state.setServerId(e?.target?.value || '') }}
          />
          <SelectInputSM
            label='Company'
            id='receivers-filter-company'
            showLabel
            className={INPUT_CLASSES}
            containerClassName={INPUT_CONTAINER_CLASSES}
            options={[{ label: 'Select Company', value: '' }, ...companies]}
            disabled={!receivers}
            hideError
            registration={{ value: state.companyId, onChange: (e: any) => state.setCompanyId(e?.target?.value || '') }}
          />
          <SelectInputSM
            label='Queue'
            id='receivers-filter-queue'
            showLabel
            className={INPUT_CLASSES}
            containerClassName={INPUT_CONTAINER_CLASSES}
            options={[{ label: 'All', value: '' }, ...RECEIVERS_QUEUE_SIZE_OPTIONS]}
            disabled={!receivers}
            hideError
            registration={{ value: state.queueSize, onChange: (e: any) => state.setQueueSize(e?.target?.value || '') }}
          />
        </div>
      </ModalBody>
    </FilterModalContainer>
  )
}
